// src/pages/warehouse/LeiterListenPage.tsx
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/api/auth'
import {
  useEvents, useResolveEvent, useTransit, useUnlinked, useOpenCounts, sendMove,
  useQuarantineReports, useResolveQuarantineReport,
  useQuarantineReportDetail, fetchQuarantineReportDetail,
  type UnlinkedPosition,
} from '@/api/warehouse'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { triggerBlobDownload } from '@/lib/download'
import { JournalView } from './JournalPage'
import { reiheAusLeerplatzMeldung } from '@/lib/leerplatz'
import { useVorabOffen } from '@/api/vorab'
import { VorabOffenListe } from './components/VorabOffenSection'
import { WarehouseCompanyFilter } from '@/components/warehouse/WarehouseCompanyFilter'
import type { WarehouseCompany } from '@/lib/warehouse-company'

const TABS = [
  { key: 'alle', label: 'Alle offenen' },
  { key: 'klaerfall', label: 'Klärfälle' },
  // Modul 29: Platz 2 ist Absicht. Die Bestandswarnung ist severity 'warn'
  // (gelb) — rot bleibt Blockaden vorbehalten, denn hier steht nichts still:
  // die Buchung lief sauber durch. Die Dringlichkeit steckt in der REIHENFOLGE.
  { key: 'bestandswarnung', label: 'Bestandswarnungen' },
  { key: 'belegt_warnung', label: 'Belegt-Warnungen' },
  { key: 'palette_ersetzt', label: 'Palette ersetzt' },
  { key: 'leerplatz_abweichung', label: 'Leerer Platz stimmt nicht' },
  { key: 'eskalation', label: 'WE-Eskalationen' },
  { key: 'unterlieferung', label: 'Unterlieferungen' },
  { key: 'mehrfach_offen', label: 'Mehrfach offen' },
  { key: 'we_mengen_abweichung', label: 'WE-Abweichungen' },
  { key: 'we_upload_fehler', label: 'WE-Upload-Fehler' },
  { key: 'unlinked', label: 'Nicht verknüpft' },
  { key: 'transit', label: 'Offene Bewegungen' },
  // Plans/2026-10-19-vorab-bestand-plan.md §2.4: Vorab aus dem WE, bis DR die Lieferung fuehrt.
  { key: 'vorab_offen', label: 'Vorab offen' },
  { key: 'quarantaene', label: 'Quarantäne' },
  // User 2026-10-15: Bewegungs-Journal als Reiter hier statt eigener Seite (Deep-Link ?tab=journal).
  { key: 'journal', label: 'Journal' },
] as const
type TabKey = (typeof TABS)[number]['key']

// User 2026-10-15: Leiter-Listen kontrastreicher. Karten weiss mit Schatten statt
// grau-auf-weiss (--panel #f9f9f9 war vom Seitenhintergrund kaum zu unterscheiden),
// Nebentexte in --ink-2 statt --muted (#999 verfehlt AA, siehe index.css).
const KARTE = 'rounded-lg border border-line bg-background p-3 shadow-sm'
/** Dringlichkeit als linker Farbbalken — auf einen Blick lesbar, statt eines 10-px-Punkts. */
const SEV_RAND: Record<string, string> = { error: 'border-l-red-600', warn: 'border-l-amber-500' }

function EventZeile({ ev, onResolve }: { ev: Record<string, unknown>; onResolve: (id: number) => void }) {
  const rand = SEV_RAND[String(ev['severity'])] ?? 'border-l-slate-400'
  const row = reiheAusLeerplatzMeldung(String(ev['message'] ?? ''))
  return (
    <li className={`${KARTE} border-l-4 ${rand} flex items-start gap-3`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink font-medium break-words">{String(ev['message'])}</div>
        {ev['eventType'] === 'leerplatz_abweichung' && <Link className="text-sm text-primary underline"
          to={row ? `/warehouse/lager-navi/leere-plaetze?reihe=${encodeURIComponent(row)}` : '/warehouse/lager-navi/leere-plaetze?tab=abweichungen'}>Regal prüfen</Link>}
        <div className="mt-0.5 text-xs text-ink-2 tabular-nums">{new Date(String(ev['createdAt'])).toLocaleString('de-DE')}</div>
      </div>
      <Button size="sm" variant="outline" onClick={() => onResolve(Number(ev['id']))}>Auflösen</Button>
    </li>
  )
}

function UnlinkedZeile({ p }: { p: UnlinkedPosition }) {
  return <li className={`${KARTE} text-sm`}>
    <b className="text-ink">{p.sourceArticle ?? p.freeText ?? p.rawCode ?? 'Artikel unbekannt'}</b>
    <div className="mt-1 text-xs text-ink-2">
      Palette: <span className="font-mono text-ink">{p.paletteNo ?? '—'}</span>
      {' · '}Platz: <span className="font-mono text-ink">{p.placeBarcode ?? 'unbekannt'}</span>
      {' · '}seit {new Date(p.createdAt).toLocaleDateString('de-DE')}
    </div>
  </li>
}

// OI-130 P3 (User-Feedback 2026-08-03): Kurzerklaerung je Liste — die Meldungstexte
// allein waren im Betrieb nicht zuordenbar. Eine Zeile pro Typ, keine Romane.
const TAB_ERKLAERUNG: Record<Exclude<TabKey, 'alle'>, string> = {
  leerplatz_abweichung: 'Leere Fächer stimmen nicht mit dem Systemstand überein. Platz vor Ort prüfen und die Warnung mit Notiz erledigen.',
  palette_ersetzt: 'Eine Palette wurde durch eine Palette mit anderem Artikel oder anderer Firma ersetzt. Die alte Palette ist ausgelagert.',
  klaerfall: 'Eine Buchung oder Import-Zeile konnte keinem Platz/keiner Palette zugeordnet werden (z.B. Alt-Picking-Abgleich) — pruefen, von Hand klaeren, dann aufloesen.',
  bestandswarnung: 'Beim Kommissionieren wurde ein anderer Rest gezaehlt, als das System erwartet hat. Der Bestand steht bereits auf dem GEZAEHLTEN Wert — nachzaehlen und klaeren, woher die Differenz kommt (Schwund, Fehlbuchung, falsche Packungsgroesse).',
  belegt_warnung: 'Eine Palette wurde auf einen Platz gescannt, der laut System schon belegt ist — vor Ort pruefen, wer wirklich dort steht.',
  eskalation: 'Ein WE-Auftrag ist nach Ablauf der Frist (Standard 3 Tage) nicht vollstaendig — Pflichtpruefung durch den Leiter.',
  unterlieferung: 'Beim Wareneingang wurde eine Minderlieferung dokumentiert (weniger/beschaedigt/falsch geliefert).',
  mehrfach_offen: 'Fuer denselben Artikel sind mehrere Anforderungen gleichzeitig offen — vermutlich doppelt angefordert.',
  we_mengen_abweichung: 'Erfasste Menge/Kartons weichen vom Soll des WE-Auftrags ab — im Auftrag Soll/Ist pruefen; stimmt das Ist, dort "Ist als Soll uebernehmen".',
  we_upload_fehler: 'Der Etiketten-PDF-Upload nach SharePoint ist fehlgeschlagen — Etiketten ggf. manuell drucken/ablegen.',
  unlinked: 'Ein Platz ist als belegt markiert, ohne dass eine Palette im System damit verknuepft ist (Fremd-Belegung).',
  transit: 'Eine Palette wurde aufgenommen, aber noch nicht wieder abgestellt — unterwegs oder vergessen.',
  vorab_offen: 'Ware aus dem Wareneingang, die schon vorab im Bestand zählt, aber in DR noch fehlt. Schließt sich selbst, sobald DR die Lieferung führt. Steht „evtl. doppelt“ da, prüfen, ob DR sie von Hand schon hat, dann „Vorab übernommen“.',
  quarantaene: 'Verworfene Offline-Buchungen der Scan-App, die nicht automatisch nachgeholt werden konnten — manuelle Bergung durch den Leiter.',
  journal: 'Alle Lagerbewegungen (nur lesen) — filterbar nach Artikel, Platz, Aktion, Wer, Gerät und Zeitraum.',
}

/** Eine offene Bewegung. Bewusst EINE Komponente fuer beide Fundstellen
 *  (Uebersicht „Alle offenen" und Reiter „Offene Bewegungen") — vorher stand
 *  derselbe Text zweimal zeichengleich im File, und eine Aenderung an nur einer
 *  Stelle haette die beiden Ansichten auseinanderlaufen lassen (Lehre aus der
 *  Divergenz in Hotfix 27.2).
 *
 *  Zur Beschriftung — DREI Stufen, und jede sagt genau so viel, wie belegt ist
 *  (Modul 33.1 / Migration 0120):
 *
 *  1. `pickedUpWorkerName` gesetzt ⇒ „Mitarbeiter: X". Das ist eine BEHAUPTUNG UEBER
 *     EINE PERSON, und sie ist jetzt gedeckt: die Spalte wird nur beschrieben, wenn
 *     der Server die `worker_id` gegen `warehouse_workers` aufgeloest hat, und sie
 *     wird zusammen mit `picked_up_by` gesetzt und geleert.
 *  2. sonst `aufnahmeLautJournal` ⇒ „Aufgenommen laut Journal: X" (Stand Modul 33).
 *     Der Name stammt aus der juengsten Aufnahme-Buchung und kann je nach Herkunft
 *     ein Mensch, ein Geraet oder ein Web-Konto sein — die Zeile nennt deshalb die
 *     QUELLE statt eine Person zu behaupten. Das ist der Fall fuer alle Paletten, die
 *     vor dieser Migration aufgenommen wurden: es gibt bewusst KEINEN Backfill, weil
 *     sich die Person nicht rekonstruieren laesst.
 *  3. sonst „Mitarbeiter nicht erfasst (Geraet: …)" — der ehrliche Geraete-Fallback
 *     aus Modul 29.
 *
 *  Die Stufen NICHT zusammenfassen: der Unterschied zwischen 1 und 2 ist genau der
 *  zwischen „wir wissen es" und „so stand es im Journal". */
/** 21.10.2026 (P-004082 hing 15 h in_transit): der Leiter bucht eine haengende Palette
 *  ueber die normale Place-Buchung auf ihren Herkunftsplatz zurueck. Gebunden an die
 *  angezeigte Aufnahme — wurde die Palette inzwischen neu aufgenommen, lehnt der Server
 *  mit `transit_changed` ab. Keine Mischpalette aus dem Web (`confirm_misch: false`). */
function Rueckbuchung({ p }: { p: Record<string, unknown> }) {
  const { warehouse } = useAuth()
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const paletteNo = p['paletteNo'] ? String(p['paletteNo']) : null
  const herkunft = p['herkunftPlatzBarcode'] ? String(p['herkunftPlatzBarcode']) : null
  const pickupMoveId = typeof p['pickupMoveId'] === 'number' ? p['pickupMoveId'] : null
  if (!warehouse.book || !paletteNo || !herkunft || pickupMoveId == null) {
    return <div className="mt-1 text-ink-2">Am Scanner Platz scannen, um die Bewegung abzuschließen.</div>
  }
  const buchen = async () => {
    if (!window.confirm(`Palette ${paletteNo} zurück auf ${herkunft} buchen?`)) return
    setBusy(true); setMeldung(null)
    try {
      const res = await sendMove('/warehouse/move/place/', {
        palette_no: paletteNo, place_barcode: herkunft, confirm_misch: false, expect_pickup_move_id: pickupMoveId,
      }, { allowOffline: false })
      if ('blocked' in res && res.blocked) { setMeldung(res.message ?? 'Buchung abgelehnt'); return }
      toast.success(`${paletteNo} zurück auf ${herkunft} gebucht`)
      await qc.invalidateQueries({ queryKey: ['warehouse', 'transit'] })
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : 'Buchung fehlgeschlagen')
    } finally { setBusy(false) }
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Button size="sm" variant="outline" disabled={busy} onClick={() => void buchen()}>
        {`Zurück auf ${herkunft} buchen`}
      </Button>
      {meldung && <span role="alert" className="text-red-600">{meldung}</span>}
    </div>
  )
}

function TransitZeile({ p }: { p: Record<string, unknown> }) {
  const person = p['pickedUpWorkerName']      // Stufe 1: belegt (Migration 0120)
  const ausJournal = p['aufnahmeLautJournal']  // Stufe 2: hergeleitet (Bestand)
  // Reihenfolge ist hier fachlich, nicht kosmetisch: „ohne Halter" wird zuerst geprueft.
  // Ein uebersprungener Klaerfall (skipWithKlaerfall) und ein zurueckgenommener Pickup
  // (cancelPickup) setzen `picked_up_by`/`picked_up_device` auf NULL — die Aufnahme-Buchung
  // im Journal bleibt aber stehen. Wer den Namen zuerst prueft, verdeckt damit genau die
  // rote Warnung, die den Leiter auf eine herrenlose Palette hinweist (Befund der
  // adversarialen Pruefung 2026-08-08, CRITICAL — die Zeile war praktisch unerreichbar
  // geworden).
  // Modul 33.1: geprueft wird `picked_up_by` UND `picked_up_device`, nicht mehr nur das
  // Geraet. Grund: `pickupPalette` schreibt `deviceLabel ?? ''`, und `device_label` ist im
  // Zod-Schema optional — eine Buchung ohne Geraeteangabe (Web, Skript) erzeugt den LEEREN
  // STRING, der falsy ist. Die Zeile meldete dann „ohne Halter" fuer eine Palette, die
  // sehr wohl gehalten wird, und verdeckte dabei die belegte Person vollstaendig
  // (Befund der adversarialen Pruefung 2026-08-08, unabhaengig von zwei Pruefern).
  //
  // `HALTER_LEER` leert alle vier Halter-Spalten gemeinsam — „kein Halter" heisst also,
  // dass WEDER eine Besitzer-Id NOCH ein Geraet dasteht. Genau das steht jetzt hier.
  const ohneHalter = p['pickedUpBy'] == null && !p['pickedUpDevice']
  // Leerer String soll nicht als „(Gerät: )" erscheinen.
  const geraet = p['pickedUpDeviceName'] ? String(p['pickedUpDeviceName'])
    : p['pickedUpDevice'] ? (String(p['pickedUpDevice']).startsWith('api:') ? 'Unbekanntes Gerät' : String(p['pickedUpDevice']))
    : 'nicht angegeben'
  if (ohneHalter) {
    return (
      <li className="rounded-lg border border-amber-400 border-l-4 bg-background p-3 text-sm text-ink shadow-sm">
        <b>{p['merchantSku'] ? <GrundartikelLink code={String(p['merchantSku'])} /> : String(p['freeText'] ?? '?')}</b> in Bewegung seit{' '}
        {new Date(String(p['updatedAt'])).toLocaleString('de-DE')}
        {' · '}<span className="text-red-600">ohne Halter (Klaerfall/uebersprungen)</span>
        <Rueckbuchung p={p} />
      </li>
    )
  }
  return (
    <li className="rounded-lg border border-amber-400 border-l-4 bg-background p-3 text-sm text-ink shadow-sm">
      <b>{p['merchantSku'] ? <GrundartikelLink code={String(p['merchantSku'])} /> : String(p['freeText'] ?? '?')}</b> in Bewegung seit{' '}
      {new Date(String(p['updatedAt'])).toLocaleString('de-DE')}
      {person
        ? <> · Mitarbeiter: <b>{String(person)}</b> (Gerät: {geraet})</>
        : ausJournal
          ? <> · Aufgenommen laut Journal: <b>{String(ausJournal)}</b> (Gerät: {geraet})</>
          : <> · Mitarbeiter nicht erfasst (Gerät: {geraet})</>}
      <Rueckbuchung p={p} />
    </li>
  )
}

/** Blob-Download der Roh-Payload — Muster wie downloadPdf() (api/warehouse.ts).
 *  A3 (Codex-Fix): die Liste liefert kein payload mehr — der Detail-Endpoint
 *  wird bei Klick nachgeladen (funktioniert auch ohne vorheriges "anzeigen"). */
async function downloadQuarantinePayload(id: number, reportId: string): Promise<void> {
  const detail = await fetchQuarantineReportDetail(id)
  const blob = new Blob([JSON.stringify(detail['payload'], null, 2)], { type: 'application/json' })
  triggerBlobDownload(blob, `quarantine-${reportId}.json`)
}

/** Eine Zeile — eigene Komponente, damit `useQuarantineReportDetail` nur fuer
 *  diese eine (aufgeklappte) Zeile aktiv ist, nicht fuer die ganze Liste. */
function QuarantaeneRow({ q, lead, notes, setNotes, onResolveQuarantine, resolvePending }: {
  q: Record<string, unknown>
  lead: boolean
  notes: Record<number, string>
  setNotes: (next: Record<number, string>) => void
  onResolveQuarantine: (id: number) => void
  resolvePending: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const id = Number(q['id'])
  const reportId = String(q['reportId'] ?? q['report_id'] ?? id)
  // A3: Detail (inkl. payload) NUR laden, wenn aufgeklappt.
  const detail = useQuarantineReportDetail(id, isOpen)

  async function onDownload() {
    setDownloading(true)
    try { await downloadQuarantinePayload(id, reportId) }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Download fehlgeschlagen') }
    finally { setDownloading(false) }
  }

  return (
    <li className="rounded-lg border border-line bg-panel p-3 space-y-2">
      <div className="flex items-start gap-3 flex-wrap">
        <span className={`mt-1 size-2.5 rounded-full shrink-0 ${q['status'] === 'resolved' ? 'bg-slate-400' : 'bg-red-600'}`} />
        <div className="flex-1 min-w-[220px] text-sm">
          <div>
            {new Date(String(q['createdAt'])).toLocaleString('de-DE')} · Quelle: <b>{String(q['source'])}</b>
            {/* Modul 29: der Mensch fuehrt, das Geraet ist Zusatz — der Leiter
                fragt „wer hat gemeldet?", nicht „welches Geraet war das?". */}
            {' · '}Mitarbeiter: <b>{String(q['workerName'] ?? 'nicht erfasst')}</b>
            {' · '}Gerät: {String(q['deviceLabel'] ?? '—')}
          </div>
          <div className="text-xs text-ink-2">
            Status: {q['status'] === 'resolved' ? 'Aufgeloest' : 'Offen'}
            {q['status'] === 'resolved' && q['resolvedByName'] ? ` von ${String(q['resolvedByName'])}` : ''}
            {q['status'] === 'resolved' && q['resolutionNote'] ? ` — ${String(q['resolutionNote'])}` : ''}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setIsOpen((v) => !v)}>
          {isOpen ? 'Payload verbergen' : 'Payload anzeigen'}
        </Button>
        <Button size="sm" variant="outline" disabled={downloading} onClick={() => void onDownload()}>
          Download
        </Button>
      </div>

      {isOpen && (
        detail.isLoading ? (
          <div className="text-xs text-ink-2 p-3">Payload wird geladen…</div>
        ) : detail.isError ? (
          <div className="text-xs text-red-600 p-3 flex items-center gap-2">
            Payload konnte nicht geladen werden.
            <Button size="sm" variant="outline" onClick={() => void detail.refetch()}>Erneut versuchen</Button>
          </div>
        ) : (
          <pre className="font-mono text-[11px] leading-relaxed text-ink-2 overflow-x-auto bg-panel-2 p-3 rounded border border-line">
            {JSON.stringify(detail.data?.['payload'], null, 2)}
          </pre>
        )
      )}

      {q['status'] !== 'resolved' && lead && (
        <div className="flex flex-wrap gap-2 items-center">
          <Input className="w-64" placeholder="Aufloesungs-Notiz (optional)"
            value={notes[id] ?? ''} onChange={(e) => setNotes({ ...notes, [id]: e.target.value })} />
          <Button size="sm" disabled={resolvePending} onClick={() => onResolveQuarantine(id)}>
            Auflösen
          </Button>
        </div>
      )}
    </li>
  )
}

// Firmenfilter: Priostahl = was der Server Priostahl zuordnet (?company=priostahl), Stahlia = alles
// uebrige. Meldungen ohne Firmenbezug (Leerplatz-Pruefungen, Scanner-Quarantaene) bleiben so bei
// Stahlia sichtbar — ein reiner ?company=stahlia-Filter liesse sie in beiden Ansichten verschwinden.
type FirmenQuelle<T> = { data?: T[]; isError: boolean }
function nachFirma<T>(company: WarehouseCompany, lead: boolean, alle: FirmenQuelle<T>, prio: FirmenQuelle<T>,
  id: (row: T) => unknown): T[] | undefined {
  if (!lead) return alle.data // Nicht-Leiter sehen serverseitig ohnehin nur Stahlia.
  if (company === 'priostahl') return prio.data
  // Erst zeigen, wenn Priostahl bekannt ist — auch bei Fehler nicht mischen (Fehlermeldung statt Liste).
  if (prio.data === undefined) return undefined
  const priostahl = new Set((prio.data ?? []).map(id))
  return alle.data?.filter((row) => !priostahl.has(id(row)))
}
const zeilenId = (row: Record<string, unknown>) => Number(row['id'])
const EREIGNIS_LIMIT = 500
// Nicht-Leiter: ohne company dieselbe Anfrage wie „alle" (React Query dedupliziert), kein Priostahl-Abruf.
const prioFuer = (lead: boolean) => (lead ? 'priostahl' as const : undefined)

/** Ereignisse, Nicht verknuepft und Offene Bewegungen in der gewaehlten Firmenansicht. */
function useFirmenListen(company: WarehouseCompany, lead: boolean, type?: string) {
  const prio = prioFuer(lead)
  // 500 (Server-Maximum) statt 100: Priostahl-Eintraege der Gesamtliste sollen aeltere Stahlia-Meldungen nicht verdraengen.
  const eventsAlle = useEvents('open', type, undefined, EREIGNIS_LIMIT), eventsPrio = useEvents('open', type, prio, EREIGNIS_LIMIT)
  const unlinkedAlle = useUnlinked(), unlinkedPrio = useUnlinked(prio)
  const transitAlle = useTransit(true), transitPrio = useTransit(true, prio)
  const alle = [eventsAlle, eventsPrio, unlinkedAlle, unlinkedPrio, transitAlle, transitPrio]
  return {
    events: nachFirma(company, lead, eventsAlle, eventsPrio, zeilenId),
    unlinked: nachFirma(company, lead, unlinkedAlle, unlinkedPrio, (p) => p.id),
    transit: nachFirma(company, lead, transitAlle, transitPrio, zeilenId),
    eventsLoading: eventsAlle.isLoading || eventsPrio.isLoading,
    isError: alle.some((q) => q.isError),
    refetch: () => { for (const q of alle) void q.refetch() },
  }
}

/** Reiter-Zaehler je Firma; Stahlia = alle offenen minus Priostahl. */
function useFirmenZaehler(company: WarehouseCompany, lead: boolean) {
  const alle = useOpenCounts(), prio = useOpenCounts(prioFuer(lead))
  if (!lead) return alle.data
  if (company === 'priostahl') return prio.data
  if (!alle.data || !prio.data) return undefined
  const p = prio.data
  return {
    events: Object.fromEntries(Object.entries(alle.data.events ?? {}).map(([t, n]) => [t, Math.max(0, n - (p?.events?.[t] ?? 0))])),
    quarantine_open: Math.max(0, (alle.data.quarantine_open ?? 0) - (p?.quarantine_open ?? 0)),
  }
}

function flacheSeiten(data: { pages: Array<{ items: Array<Record<string, unknown>> }> } | undefined, seiten?: number) {
  if (!data) return undefined
  const byId = new Map<number, Record<string, unknown>>()
  for (const r of data.pages.slice(0, seiten).flatMap((p) => p.items)) byId.set(zeilenId(r), r)
  return [...byId.values()]
}

/** Quarantaene-Liste je Firma. Blaettern folgt der angezeigten Quelle (Priostahl- bzw. Gesamtliste). */
function useFirmenQuarantaene(status: 'open' | 'resolved', company: WarehouseCompany, lead: boolean) {
  const alle = useQuarantineReports(status), prio = useQuarantineReports(status, prioFuer(lead))
  // Priostahl ist Teilmenge der Gesamtliste (gleiche Sortierung, gleiche Seitengroesse): k Priostahl-Seiten
  // decken alle Priostahl-Faelle der ersten k Gesamtseiten ab. Fehlt eine Priostahl-Seite (Fehler beim
  // Blaettern), bleiben die weiteren Gesamtseiten zurueck, bis sie nachgeladen ist — sonst mischt Stahlia.
  const gedeckt = lead && prio.hasNextPage ? prio.data?.pages.length ?? 0 : undefined
  const prioFehlt = company !== 'priostahl' && gedeckt !== undefined && (alle.data?.pages.length ?? 0) > gedeckt
  // E1 (Codex-Fix3, MEDIUM, 2026-07-16): alle bereits geladenen Seiten
  // flatten + nach id deduplizieren (letztes Vorkommen gewinnt — relevant nach
  // Refetch/Invalidierung, wenn sich ein Eintrag zwischen Seiten verschiebt).
  const alleRows = useMemo(() => flacheSeiten(alle.data, gedeckt), [alle.data, gedeckt])
  const prioRows = useMemo(() => flacheSeiten(prio.data), [prio.data])
  const rows = nachFirma(company, lead, { data: alleRows, isError: alle.isError }, { data: prioRows, isError: prio.isError }, zeilenId)
  const quelle = lead && company === 'priostahl' ? prio : alle
  // Erst die geladenen Priostahl-Seiten auffrischen (neue Faelle oben, die alle.refetch mitbringt), dann die fehlende nachholen.
  // refetch() erfuellt sein Promise auch bei Fehler — dann nicht weiterblaettern, die Fehlermeldung bleibt.
  const prioNachholen = () => { void prio.refetch().then((r) => { if (!r.isError) void prio.fetchNextPage() }) }
  return {
    rows: rows ?? [], bereit: rows !== undefined, isError: alle.isError || prio.isError,
    weitere: quelle.hasNextPage || prioFehlt, laedt: quelle.isFetchingNextPage || prio.isFetchingNextPage,
    refetch: () => { void alle.refetch(); if (prioFehlt) prioNachholen(); else void prio.refetch() },
    weitereLaden: () => {
      if (prioFehlt) { prioNachholen(); return }
      void quelle.fetchNextPage(); if (quelle === alle && lead && prio.hasNextPage) void prio.fetchNextPage()
    },
  }
}

/** `showStatusFilter=false` (Uebersicht „Alle offenen"): fest auf offene Faelle, ohne den
 *  Offen/Aufgeloest-Umschalter — aufgeloeste Faelle gehoeren in den eigenen Reiter. */
function QuarantaeneSection({ lead, company, showStatusFilter = true }: { lead: boolean; company: WarehouseCompany; showStatusFilter?: boolean }) {
  const [status, setStatus] = useState<'open' | 'resolved'>('open')
  const [notes, setNotes] = useState<Record<number, string>>({})
  const { rows, bereit, weitere, laedt, isError: quarantineError, refetch, weitereLaden } = useFirmenQuarantaene(status, company, lead)
  const resolveQuarantine = useResolveQuarantineReport()

  async function onResolveQuarantine(id: number) {
    try {
      await resolveQuarantine.mutateAsync({ id, resolution_note: notes[id] })
      toast.success('Aufgeloest')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen') }
  }

  return (
    <div className="space-y-2">
      {showStatusFilter && (
        <div className="flex flex-wrap gap-1">
          {(['open', 'resolved'] as const).map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              aria-pressed={status === s}
              className={`px-3 py-1 rounded-md text-xs font-medium ${status === s ? 'bg-ink text-background' : 'text-ink-2 hover:bg-panel-2 hover:text-ink'}`}>
              {s === 'open' ? 'Offen' : 'Aufgeloest'}
            </button>
          ))}
        </div>
      )}

      {quarantineError && (
        <div className="text-sm text-red-600 flex items-center gap-2">
          Quarantäne-Liste konnte nicht geladen werden.
          <Button size="sm" variant="outline" onClick={refetch}>Erneut versuchen</Button>
        </div>
      )}

      <ul className="space-y-2">
        {rows.map((q) => (
          <QuarantaeneRow
            key={Number(q['id'])} q={q} lead={lead} notes={notes} setNotes={setNotes}
            onResolveQuarantine={(id) => void onResolveQuarantine(id)}
            resolvePending={resolveQuarantine.isPending}
          />
        ))}
        {rows.length === 0 && bereit && (
          <li className="text-ink-2 text-sm p-3">
            {status === 'open' ? 'Keine offenen Quarantäne-Faelle. 👍' : 'Keine aufgeloesten Quarantäne-Faelle.'}
          </li>
        )}
      </ul>

      {weitere && (
        <div className="flex justify-center pt-1">
          <Button size="sm" variant="outline" disabled={laedt}
            onClick={weitereLaden}>
            {laedt ? 'Lade…' : 'Weitere laden'}
          </Button>
        </div>
      )}
    </div>
  )
}

/** Anzeigename je Event-Typ — dieselben Begriffe wie die Reiter, damit ein Eintrag in
 *  Uebersicht und Reiter gleich heisst. */
const EVENT_LABELS: Record<string, string> = {
  leerplatz_abweichung: 'Leerer Platz stimmt nicht',
  palette_ersetzt: 'Palette ersetzt',
  klaerfall: 'Klärfälle',
  bestandswarnung: 'Bestandswarnungen',
  belegt_warnung: 'Belegt-Warnungen',
  eskalation: 'WE-Eskalationen',
  unterlieferung: 'Unterlieferungen',
  mehrfach_offen: 'Mehrfach offen',
  we_mengen_abweichung: 'WE-Abweichungen',
  we_upload_fehler: 'WE-Upload-Fehler',
  // Modul 29: Beide Typen gibt es seit Migration 0092, sie hatten aber nie ein
  // Label — in „Alle offenen" stand deshalb der rohe Typname als Ueberschrift.
  // Bewusst NUR Labels, keine eigenen Reiter.
  artikel_ausverkauft: 'Kein Bestand mehr',
  pick_leer: 'Palette leer gemeldet',
}
// Reihenfolge = Dringlichkeit: erst die Faelle, die jemanden blockieren, dann Zustandslisten.
// Modul 29: 'bestandswarnung' auf Platz 2 — sie blockiert niemanden, aber sie ist
// der einzige Hinweis darauf, dass Ware fehlt, die niemand vermisst.
const EVENT_ORDER = [
  'klaerfall', 'bestandswarnung', 'we_upload_fehler', 'eskalation', 'belegt_warnung',
  'we_mengen_abweichung', 'unterlieferung', 'mehrfach_offen',
  'artikel_ausverkauft', 'pick_leer',
  'palette_ersetzt',
  'leerplatz_abweichung',
]

function Gruppe({ titel, anzahl, children }: { titel: string; anzahl: number; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-base font-semibold text-ink flex items-center gap-2">
        {titel}
        <span className="rounded-full bg-panel-2 border border-line px-2 py-0.5 text-xs font-semibold text-ink tabular-nums">{anzahl}</span>
      </h2>
      {children}
    </section>
  )
}

/** Uebersicht „Alle offenen": zeigt JEDE offene Meldung ohne Reiter-Klick. Nur Kategorien
 *  mit tatsaechlich offenen Eintraegen erscheinen — abgearbeitete verschwinden hier und
 *  bleiben ueber die Reiter erreichbar. */
function AlleOffenenSection({ lead, company, onResolve }: {
  lead: boolean
  company: WarehouseCompany
  onResolve: (id: number) => void
}) {
  const listen = useFirmenListen(company, lead)       // ohne type -> ALLE offenen Event-Typen
  // Gleicher queryKey wie in QuarantaeneSection -> React Query dedupliziert, kein Doppel-Fetch.
  // Hier nur fuer Anzahl + „gibt es ueberhaupt welche?" (Gruppe sonst ausblenden).
  const quarantineCount = useFirmenQuarantaene('open', company, lead).rows.length
  // Vorab kommt aus DR und gehoert damit zu Stahlia — auch geladene Daten nur dort zeigen.
  const vorab = useVorabOffen(lead && company === 'stahlia')

  const eventGruppen = useMemo(() => {
    const byType = new Map<string, Array<Record<string, unknown>>>()
    for (const ev of listen.events ?? []) {
      const t = String(ev['eventType'] ?? 'sonstige')
      byType.set(t, [...(byType.get(t) ?? []), ev])
    }
    const bekannt = EVENT_ORDER.filter((t) => byType.has(t))
    const rest = [...byType.keys()].filter((t) => !EVENT_ORDER.includes(t)).sort()
    return [...bekannt, ...rest].map((t) => ({ typ: t, items: byType.get(t) ?? [] }))
  }, [listen.events])

  const transitRows = listen.transit ?? []
  const unlinkedRows = listen.unlinked ?? []
  const offeneEvents = listen.events?.length ?? 0
  const vorabRows = company === 'stahlia' ? vorab.data ?? [] : []
  const gesamt = offeneEvents + transitRows.length + unlinkedRows.length + quarantineCount + vorabRows.length
  const ladeFehler = listen.isError

  return (
    <div className="space-y-5">
      {ladeFehler && (
        <div className="text-sm text-red-600 flex items-center gap-2">
          Nicht alle Listen konnten geladen werden.
          <Button size="sm" variant="outline" onClick={listen.refetch}>Erneut versuchen</Button>
        </div>
      )}

      {gesamt === 0 && listen.events !== undefined && !ladeFehler && (
        <div className={`${KARTE} p-4 text-sm text-ink-2`}>Nichts offen. 👍</div>
      )}

      {eventGruppen.map(({ typ, items }) => (
        <Gruppe key={typ} titel={EVENT_LABELS[typ] ?? typ} anzahl={items.length}>
          <ul className="space-y-2">
            {items.map((ev) => <EventZeile key={String(ev['id'])} ev={ev} onResolve={onResolve} />)}
          </ul>
        </Gruppe>
      ))}

      {transitRows.length > 0 && (
        <Gruppe titel="Offene Bewegungen" anzahl={transitRows.length}>
          <ul className="space-y-2">
            {transitRows.map((p) => <TransitZeile key={String(p['id'])} p={p} />)}
          </ul>
        </Gruppe>
      )}

      {unlinkedRows.length > 0 && (
        <Gruppe titel="Nicht verknüpft" anzahl={unlinkedRows.length}>
          <ul className="space-y-2">
            {unlinkedRows.map((p) => <UnlinkedZeile key={p.id} p={p} />)}
          </ul>
        </Gruppe>
      )}

      {vorabRows.length > 0 && (
        <Gruppe titel="Vorab offen" anzahl={vorabRows.length}>
          <VorabOffenListe rows={vorabRows} lead={lead} />
        </Gruppe>
      )}

      {quarantineCount > 0 && (
        <Gruppe titel="Quarantäne" anzahl={quarantineCount}>
          <QuarantaeneSection lead={lead} company={company} showStatusFilter={false} />
        </Gruppe>
      )}
    </div>
  )
}

export default function LeiterListenPage() {
  const { warehouse } = useAuth()
  // Default „Alle offenen": der Leiter sieht jede Meldung sofort, ohne Reiter zu klicken.
  // ?tab=journal: Ziel der Weiterleitung von /warehouse/journal — nur Startwert, danach lokaler Zustand.
  const [params] = useSearchParams()
  const [tab, setTab] = useState<TabKey>(() => TABS.find((t) => t.key === params.get('tab'))?.key ?? 'alle')
  // Firmenfilter: beim Oeffnen immer Stahlia (bewusst nicht gespeichert). Priostahl-Meldungen
  // (TR05, TRs10, Lager 2) stehen nur in der Priostahl-Ansicht.
  const [company, setCompany] = useState<WarehouseCompany>('stahlia')
  const isEventTab = tab !== 'alle' && tab !== 'unlinked' && tab !== 'transit' && tab !== 'quarantaene' && tab !== 'journal'
    && tab !== 'vorab_offen'
  const listen = useFirmenListen(company, warehouse.lead, isEventTab ? tab : undefined)
  // Endpoint ist Leiter-only (requireWarehouse('lead')) — ohne Leiterrecht gar nicht erst laden.
  // Vorab kommt aus DR und gehoert damit zu Stahlia.
  const vorab = useVorabOffen(warehouse.lead && company === 'stahlia')
  const resolve = useResolveEvent()
  // OI-130 P3 + Codex F3: Zaehler an den Reitern aus dem COUNT-Endpoint — die
  // Listen-Antworten sind auf 100 gedeckelt und wuerden aeltere Eintraege im
  // Zaehler verstecken. unlinked/transit sind ungedeckelte Voll-Listen, deren
  // Laenge bleibt als Zaehler korrekt.
  const openCounts = useFirmenZaehler(company, warehouse.lead)
  const tabCounts = useMemo(() => {
    const ev = openCounts?.events ?? {}
    const counts: Partial<Record<TabKey, number>> = {
      unlinked: (listen.unlinked ?? []).length,
      transit: (listen.transit ?? []).length,
      quarantaene: openCounts?.quarantine_open ?? 0,
      vorab_offen: company === 'stahlia' ? (vorab.data ?? []).length : 0,
    }
    for (const t of TABS) {
      if (t.key === 'alle' || t.key === 'journal' || counts[t.key] !== undefined) continue
      counts[t.key] = ev[t.key] ?? 0
    }
    counts.alle = Object.values(ev).reduce((s, n) => s + n, 0)
      + counts.unlinked! + counts.transit! + counts.quarantaene! + counts.vorab_offen!
    return counts
  }, [openCounts, listen.unlinked, listen.transit, vorab.data, company])

  async function onResolve(id: number) {
    const note = window.prompt('Aufloesungs-Notiz (Pflicht — was wurde geklaert)?')
    if (!note || note.length < 2) return
    try { await resolve.mutateAsync({ id, note }); toast.success('Aufgeloest') }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen') }
  }

  return (
    <div className={`p-4 space-y-3 mx-auto ${tab === 'journal' ? 'max-w-7xl' : 'max-w-5xl'}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-xl font-bold">Leiter-Listen</h1>
          {/* Das Journal hat seinen eigenen Firmenfilter. */}
          {tab !== 'journal' && <WarehouseCompanyFilter value={company} onChange={setCompany} buttons includeBoth={false} />}
        </div>
        <Link to="/warehouse/mitarbeiter" className="text-sm text-primary underline">
          PINs &amp; Freischaltungen (Inventur/Wareneingang) →
        </Link>
      </div>
      <div className="flex flex-wrap gap-1 rounded-lg border border-line bg-panel-2 p-1">
        {TABS.map((t) => {
          const n = tabCounts[t.key] ?? 0
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              aria-pressed={tab === t.key}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary ${
                tab === t.key ? 'bg-ink text-background shadow-sm' : 'text-ink-2 hover:bg-background hover:text-ink'}`}>
              {t.label}
              {/* OI-130 P3: offene Anzahl direkt am Reiter — "sind da noch welche?" ohne Klick. */}
              {n > 0 && (
                <span className={`ml-1.5 inline-block rounded-full px-1.5 text-xs font-semibold tabular-nums align-middle border bg-background text-ink ${
                  tab === t.key ? 'border-transparent' : 'border-line'
                }`}>{n}</span>
              )}
            </button>
          )
        })}
      </div>

      {/* OI-130 P3: Kurz-Legende. Auf dem aktiven Reiter steht die Erklaerung direkt darueber;
          "Alle offenen" bekommt die aufklappbare Gesamt-Legende. */}
      {tab !== 'alle' && (
        <p className="text-sm text-ink-2 max-w-3xl border-l-2 border-primary pl-3">{TAB_ERKLAERUNG[tab]}</p>
      )}
      {tab === 'alle' && (
        <details className="text-sm text-ink-2 max-w-3xl">
          <summary className="cursor-pointer select-none">Was bedeutet was? (Legende)</summary>
          <ul className="mt-1 space-y-1 list-disc pl-4">
            {TABS.filter((t) => t.key !== 'alle').map((t) => (
              <li key={t.key}><b>{t.label}:</b> {TAB_ERKLAERUNG[t.key as Exclude<TabKey, 'alle'>]}</li>
            ))}
          </ul>
        </details>
      )}

      {tab === 'alle' && (
        <AlleOffenenSection lead={warehouse.lead} company={company} onResolve={(id) => void onResolve(id)} />
      )}

      {isEventTab && (
        <ul className="space-y-2">
          {(listen.events ?? []).map((ev) => <EventZeile key={String(ev['id'])} ev={ev} onResolve={(id) => void onResolve(id)} />)}
          {listen.events?.length === 0 && <li className="text-ink-2 text-sm p-3">Nichts offen. 👍</li>}
        </ul>
      )}

      {tab === 'unlinked' && (
        <ul className="space-y-2">
          {(listen.unlinked ?? []).map((p) => <UnlinkedZeile key={p.id} p={p} />)}
          {listen.unlinked?.length === 0 && <li className="text-ink-2 text-sm p-3">Keine unverknuepften Belegungen.</li>}
        </ul>
      )}

      {tab === 'transit' && (
        <ul className="space-y-2">
          {(listen.transit ?? []).map((p) => <TransitZeile key={String(p['id'])} p={p} />)}
          {listen.transit?.length === 0 && <li className="text-ink-2 text-sm p-3">Keine offenen Bewegungen.</li>}
        </ul>
      )}

      {tab === 'quarantaene' && <QuarantaeneSection lead={warehouse.lead} company={company} />}
      {tab === 'vorab_offen' && (
        company !== 'stahlia'
          ? <div className="text-sm text-ink-2 p-3">Vorab gibt es nur bei Stahlia.</div>
          : vorab.isError
          ? <div className="text-sm text-red-600">Vorab-Liste konnte nicht geladen werden.</div>
          : <VorabOffenListe rows={vorab.data ?? []} lead={warehouse.lead} leer="Kein Vorab offen." />
      )}
      {/* Leiter-Reiter: standardmaessig das GESAMTE Journal — die Firmen-Gruppen blenden
          Bewegungen ohne Position (Handregal-Zaehlung, Anforderungen …) aus. */}
      {tab === 'journal' && <JournalView defaultCompany="neutral" />}
      {/* OI-130 P5: Der Sicherung-Block ist auf die eigene Seite /warehouse/sicherung
          umgezogen (Sidebar LAGER, ganz unten) — hier stand er unauffindbar unter den Listen. */}
    </div>
  )
}
